import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Customize_Css/Pagination.css';
import '../Customize_Css/ViewVendor.css';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTrashAlt, FaFileExport, FaEdit, FaEye, FaSearch, FaUser } from 'react-icons/fa';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { ToastContainer, toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const ViewEmployee = () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const epicxTenantId = localStorage.getItem('epicxTenantId');
    const AuthorizationToken = localStorage.getItem('token');
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // State for managing modal visibility and selected employee
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${BASE_URL}/login/api/login/employees`, {
                    headers: {
                        Authorization: `Bearer ${AuthorizationToken}`,
                    },
                });
                const result = await response.json();

                if (response.ok) {
                    setData(result.employees);
                    setOriginalData(result.employees);
                } else {
                    setError(result.message || 'Failed to fetch data');
                }
            } catch (err) {
                setError('Error fetching data: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [BASE_URL, AuthorizationToken]);

    // Search
    const handleSearch = (value) => {
        setSearchTerm(value.toLowerCase());

        if (value.trim() === '') {
            setData(originalData);
        } else {
            const filteredData = originalData.filter((item) =>
                Object.keys(item).some((key) =>
                    item[key]?.toString().toLowerCase().includes(value.toLowerCase())
                )
            );
            setData(filteredData);
        }
        setCurrentPage(1);
    };

    // Sorting of columns
    const handleSort = (key) => {
        // Skip sorting for 'actions' column
        if (key === 'actions') return;
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setData((prevData) => {
            return [...prevData].sort((a, b) => {
                const aValue = a[key] || '';
                const bValue = b[key] || '';
                if (aValue < bValue) {
                    return direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        });
    };

    // Select all items for actions like delete
    const handleSelectAll = () => {
        if (selectedItems.length === data.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(data.map((item) => item._id));
        }
    };

    // Selecting individual items
    const handleSelectItem = (_id) => {
        if (selectedItems.includes(_id)) {
            setSelectedItems(selectedItems.filter((item) => item !== _id));
        } else {
            setSelectedItems([...selectedItems, _id]);
        }
    };

    // Delete
    const handleDelete = async () => {
        if (selectedItems.length > 0) {
            try {
                const confirmDelete = window.confirm(
                    `Are you sure you want to delete ${selectedItems.length} item(s)?`
                );
                if (!confirmDelete) return;

                const promises = selectedItems.map(async (itemId) => {
                    const response = await fetch(
                        `${BASE_URL}/orders/api/employees/${epicxTenantId}/employee/${itemId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${AuthorizationToken}`,
                            },
                            method: 'DELETE',
                        }
                    );
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(
                            `Failed to delete item with ID ${itemId}: ${errorData.message}`
                        );
                    }
                });

                await Promise.all(promises);
                toast.success('Selected items deleted successfully.');
                setData((prevData) =>
                    prevData.filter((item) => !selectedItems.includes(item._id))
                );
                setSelectedItems([]);
            } catch (error) {
                console.error('Error deleting items:', error);
                toast.error('Failed to delete selected items. Please try again.');
            }
        } else {
            toast.error('No items selected for deletion.');
        }
    };

    // Edit
    const handleEdit = () => {
        if (selectedItems.length === 1) {
            const selectedItem = data.find((item) => item._id === selectedItems[0]);
            if (selectedItem) {
                navigate(`/update-employee/${selectedItem._id}`, { state: { employee: selectedItem } });
            } else {
                toast.error('Error: Selected item not found.');
            }
        } else {
            toast.error('Please select exactly one employee to edit');
        }
    };

    // Export
    const handleExport = () => {
        const dataToExport = selectedItems.length > 0
            ? data.filter((item) => item._id && selectedItems.includes(item._id))
            : data;
        const exportData = dataToExport.map((item) => ({
            employeeId: item.employeeId,
            employeeName: item.employeeName,
            phoneNumber: item.phoneNumber,
            emailId: item.emailId,
            designation: item.designation,
            department: item.department,
            city: item.city,
            hireDate: item.hireDate ? new Date(item.hireDate).toLocaleDateString() : '-',
            gender: item.gender,
            address: item.address,
            statusCode: item.statusCode,
            tenantId: item.tenantId,
            createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-',
            updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-',
            __v: item.__v,
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Employees');
        XLSX.writeFile(wb, 'employee_data.xlsx');
    };

    // Handle View More button click
    const handleViewMore = (employee) => {
        setSelectedEmployee(employee);
        setShowModal(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEmployee(null);
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentData = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    // Define columns to display, including actions
    const columns = [
        'employeeId',
        'employeeName',
        'phoneNumber',
        'emailId',
        'designation',
        // 'department',
        // 'gender',
        'city',
        'hireDate',
        'address',
        'actions',
    ];

    // Inline styles for table to ensure alignment
    const tableStyles = {
        tableContainer: {
            overflowX: 'auto', // Enable horizontal scrolling for wide tables
        },
        tableHeader: {
            display: 'grid',
            gridTemplateColumns: `50px repeat(${columns.length - 1}, minmax(120px, 1fr)) 100px`, // Fixed widths for checkbox and actions
            alignItems: 'center',
            height: '65px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #dee2e6',
        },
        tableRow: {
            display: 'grid',
            gridTemplateColumns: `50px repeat(${columns.length - 1}, minmax(120px, 1fr)) 100px`, // Match header column widths
            alignItems: 'center',
            borderBottom: '1px solid #dee2e6',
            padding: '8px 0',
        },
        cell: {
            padding: '8px',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', // Prevent text wrapping
        },
        checkboxCell: {
            padding: '8px',
            textAlign: 'center',
        },
        headerCell: {
            padding: '8px',
            fontWeight: 'bold',
            cursor: 'pointer',
            textAlign: 'left',
        },
        actionCell: {
            padding: '8px',
            textAlign: 'center',
        },
        selectedRow: {
            backgroundColor: '#e9ecef',
        },
        modalBody: {
            maxHeight: '400px',
            overflowY: 'auto',
        },
        viewMoreButton: {
            backgroundColor: '#31a87e',
            color: 'white',
            padding: '2px 8px',
            fontSize: '12px',
            border: 'none',
            borderRadius: '4px',
        },
    };

    // All possible employee fields for the modal
    const allFields = [
        'employeeId',
        'employeeName',
        'phoneNumber',
        'emailId',
        'designation',
        'department',
        'gender',
        'city',
        'hireDate',
        'address',
        'statusCode',
        'tenantId',
        'createdAt',
        'updatedAt',
        '__v',
    ];

    return (
        <>
            <Sidebar />
            <div className="vendors-page">
                <div>
                    <header className="vendors-hero">
                        <div>
                            <p className="vendors-kicker">People</p>
                            <h1>Employee List</h1>
                            <p>Browse, find, and manage employees.</p>
                        </div>
                        <button className="vendors-primary-btn" onClick={() => navigate('/add-employee')}>
                            <FaPlus /> Add Employee
                        </button>
                    </header>
                    <section className="vendors-toolbar">
                        <div className="vendors-search">
                            <FaSearch aria-hidden="true" />
                            <input type="text" className="form-control" placeholder="Search employees..." value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
                        </div>
                        <Dropdown>
                            <Dropdown.Toggle className="vendors-action-btn">Action</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handleDelete} disabled={selectedItems.length === 0}>
                                    <FaTrashAlt className="me-2" /> Delete
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleExport}>
                                    <FaFileExport className="me-2" /> Export
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </section>
                    {loading && <div>Loading...</div>}
                    {error && <div style={{ color: 'red' }}>{error}</div>}

                    {!loading && !error && (
                        <div className="vendors-card">
                            <div className="vendors-card-head">
                                <div>
                                    <h2>All Employees</h2>
                                    <p>Select an employee to edit, delete, or view full details.</p>
                                </div>
                                <span className="vendors-count"><FaUser aria-hidden="true" /> {data.length} {data.length === 1 ? 'employee' : 'employees'}</span>
                            </div>
                            <div className="vendors-table-wrap">
                                <table className="vendors-table">
                                    <thead>
                                        <tr>
                                            <th className="vendors-check-cell">
                                                <input className="vendors-checkbox" type="checkbox" checked={selectedItems.length === data.length && data.length > 0} onChange={handleSelectAll} />
                                            </th>
                                            {columns.filter((key) => key !== 'actions').map((key) => (
                                                <th key={key} onClick={() => handleSort(key)}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                                                    {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                                                </th>
                                            ))}
                                            <th className="vendors-action-cell">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentData.length > 0 ? currentData.map((item) => (
                                            <tr key={item._id} className={selectedItems.includes(item._id) ? 'is-selected' : ''}>
                                                <td className="vendors-check-cell">
                                                    <input className="vendors-checkbox" type="checkbox" checked={selectedItems.includes(item._id)} onChange={() => handleSelectItem(item._id)} />
                                                </td>
                                                {columns.filter((key) => key !== 'actions').map((key) => (
                                                    <td key={key}>
                                                        {key === 'employeeName' ? <span className="vendor-name">{item[key] || '-'}</span> : key === 'hireDate' ? (item[key] ? new Date(item[key]).toLocaleDateString() : '-') : (item[key] || '-')}
                                                    </td>
                                                ))}
                                                <td className="vendors-action-cell">
                                                    <div className="vendors-action-group">
                                                        <button type="button" className="vendors-icon-btn vendors-icon-btn--view" title="View details" aria-label={`View ${item.employeeName}`} onClick={() => handleViewMore(item)}><FaEye aria-hidden="true" /></button>
                                                        <button type="button" className="vendors-icon-btn vendors-icon-btn--edit" title="Edit employee" aria-label={`Edit ${item.employeeName}`} onClick={() => navigate(`/update-employee/${item._id}`, { state: { employee: item } })}><FaEdit aria-hidden="true" /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan={columns.length + 1}><div className="vendors-empty"><h3>No employees found</h3><p>Try another search or add an employee.</p></div></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="vendors-card-footer">
                                <span className="vendors-page-summary">{data.length > 0 ? `Showing ${indexOfFirstItem + 1}–${Math.min(indexOfLastItem, data.length)} of ${data.length}` : 'Showing 0 employees'}</span>
                                <div className="vendors-pagination">
                                    <ul className="pagination mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}><button className="page-link btn-sm" onClick={() => setCurrentPage(1)}>&laquo;</button></li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}><button className="page-link btn-sm" onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}>&lt;</button></li>
                                        <li className="page-item active"><button className="page-link btn-sm">{currentPage}</button></li>
                                        <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}><button className="page-link btn-sm" onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages || 1))}>&gt;</button></li>
                                        <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}><button className="page-link btn-sm" onClick={() => setCurrentPage(totalPages || 1)}>&raquo;</button></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Modal for displaying employee details */}
                    <Modal show={showModal} onHide={handleCloseModal} size="lg" centered dialogClassName="vendor-details-modal">
                        <Modal.Header closeButton>
                            <Modal.Title>{selectedEmployee?.employeeName || 'Employee Details'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedEmployee && (
                                <div className="vendor-details-content">
                                    <div className="vendor-details-title">
                                        <span><FaUser aria-hidden="true" /></span>
                                        <div>
                                            <small>Employee</small>
                                            <h3>{selectedEmployee.employeeName || '-'}</h3>
                                            <p>{selectedEmployee.designation || 'No designation'}</p>
                                        </div>
                                    </div>
                                    <div className="vendor-details-grid">
                                        {allFields.filter((key) => key !== '__v').map((key) => (
                                            <div key={key} className={key === 'address' ? 'details-wide' : undefined}>
                                                <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</span>
                                                <strong>
                                                    {['hireDate', 'createdAt', 'updatedAt'].includes(key)
                                                        ? (selectedEmployee[key] ? new Date(selectedEmployee[key]).toLocaleDateString() : '-')
                                                        : (selectedEmployee[key] || '-')}
                                                </strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button className="vendors-modal-close" onClick={handleCloseModal}>Close</Button>
                        </Modal.Footer>
                    </Modal>
                </div>
                <div className="vendors-footer"><Footer /></div>
                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={true}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            </div>
        </>
    );
};

export default ViewEmployee;